/**
 * Created by Ning Jiang on 2023-09-01.
 */
import {DCRoadReelsView} from "../reels/DCRoadReelsView";
import {DevilsCrossRoadLayers, Layers} from "./DevilsCrossRoadLayers";
import {DevilsCrossRoad} from "../DevilsCrossRoad";
import {gsap, TimelineLite} from "gsap";
import {DevilsCrossRoadGameAssets} from "../DevilsCrossRoadGameAssets";
import {DevilsCrossRoadSound} from "../sound/DevilsCrossRoadSound";

export class MultiplierContainer extends PIXI.Container {

    public value:number;
    private _bg:PIXI.Sprite;
    private _text:PIXI.BitmapText;

    private _minWidth:number = 40;

    private _forbidden:boolean = false;
    public get forbidden() {
        return this._forbidden;
    }

    public set forbidden(value) {
        if(value == this._forbidden) {
            return;
        }
        this._forbidden = value;
        this.updateDisplay();
    }

    constructor(position:[number, number]) {
        super();
        this._bg = new PIXI.Sprite(DevilsCrossRoad.assetLoader.getTextures("MultPlate")[0]);
        this._bg.anchor.set(0.5, 0.5);
        this._bg.position.set(position[0], position[1]);
        DevilsCrossRoadLayers.getLayer(Layers.BACKGROUND_MULTIPLIERS)?.addChild(this._bg);

        this._text = new PIXI.BitmapText("", {
            fontName : DevilsCrossRoadGameAssets.BITMAP_FONT_BASE_MULTI,
            fontSize : 42,
            tint : 0xffffff
        });
        (<PIXI.Point>this._text.anchor).set(0.56, 0.46);
        this.addChild(this._text);
        this.position.set(position[0], position[1]);

        this.updateValue(1);
    }

    public updateValue(mul:number):void {
        this.value = mul;
        this._text.text = `x${mul.toString()}`;
        this.updateDisplay();
    }

    private updateDisplay():void {
        if(this._forbidden) {
            this._text.tint = 0x333333;
            DevilsCrossRoadLayers.getLayer(Layers.BACKGROUND_MULTIPLIERS)?.addChild(this);
            this._text.scale.set(0.7);
            return;
        }
        if(this.value === 1) {
            this._text.tint = 0x999999;
            DevilsCrossRoadLayers.getLayer(Layers.BACKGROUND_MULTIPLIERS)?.addChild(this);
            this._text.scale.set(0.7);
            return;
        }
        this._text.tint = 0xffffff;
        DevilsCrossRoadLayers.getLayer(Layers.MULTIPLIERS)?.addChild(this);
        this._text.scale.set(1);
    }

    public setDim():void {
        if(this._forbidden) {
            return;
        }
        this._text.tint = 0x999999;
        DevilsCrossRoadLayers.getLayer(Layers.BACKGROUND_MULTIPLIERS)?.addChild(this);
        this._text.scale.set(0.7);
    }

    public show(value:boolean):void {
        this._bg.visible = value;
        this.visible = value;
    }
}

export class MainGameMultipliers {

    // This order is the server win devWay id.
    public static POSITIONS:[number, number][] = [
        [DCRoadReelsView.REELS_CENTER[0] - 180, DCRoadReelsView.REELS_CENTER[1] + 60],
        [DCRoadReelsView.REELS_CENTER[0] - 60, DCRoadReelsView.REELS_CENTER[1] - 180],
        [DCRoadReelsView.REELS_CENTER[0] + 60, DCRoadReelsView.REELS_CENTER[1] + 180],
        [DCRoadReelsView.REELS_CENTER[0] + 180, DCRoadReelsView.REELS_CENTER[1] - 60]
    ];

    public multipliers:MultiplierContainer[] = [];

    constructor() {
        MainGameMultipliers.POSITIONS.forEach((position, id) => {
            const mul = new MultiplierContainer(position);
            mul.updateValue(DevilsCrossRoad.model.nextQuadMultiplier[id]);
            this.multipliers.push(mul);
        });
    }

    public setAll(numbers:number[], resetForbidden:boolean, doVerify:boolean):void {
        if(doVerify && !this.verify(numbers)) {
            debugger;
        }
        this.multipliers.forEach((mul, id) => {
            mul.updateValue(numbers[id]);
            if(resetForbidden) {
                mul.forbidden = false;
            }
        });
    }

    public verify(numbers:number[]):boolean {
        return this.multipliers.every((mul, id) => mul.value === numbers[id]);
    }

    public addWinMultiplierToTimeline(devWay:number, position:PIXI.Point, symbolTl:TimelineLite, multiTl:TimelineLite):void {
        const mulText = new PIXI.BitmapText("+1", {
            fontName : DevilsCrossRoadGameAssets.BITMAP_FONT_BASE_MULTI,
            fontSize : 34,
            tint : 0xffffff
        });
        (<PIXI.Point>mulText.anchor).set(0.56, 0.46);
        mulText.position.set(position.x, position.y);
        const toPositions = MainGameMultipliers.POSITIONS[devWay];
        symbolTl.add(() => {
            DevilsCrossRoadLayers.getLayer(Layers.WIN_COUNT_UP)?.addChild(mulText);
        });
        multiTl.add([
            () => {
                DevilsCrossRoadSound.soundPlayer.playTrack({name : DevilsCrossRoadSound.MULTIPLIER_MOVING});
                const targetMul = DevilsCrossRoad.model.nextQuadMultiplier[devWay];
                if(targetMul > 4) {
                    DevilsCrossRoad.backgroundElements.setMainFrame("glow_fast", false);
                }
            },
            gsap.to(mulText, {duration : 0.6, x : toPositions[0], y : toPositions[1], ease : "power2.out"})
        ]);
        multiTl.add(() => {
            mulText.parent.removeChild(mulText);
            mulText.destroy();
            this.multipliers[devWay].updateValue(this.multipliers[devWay].value + 1);
        });
    }

    public forbiddenMultiplier(devWay:number):void {
        this.multipliers[devWay].forbidden = true;
    }

    public dimAllMultipliers():void {
        this.multipliers.forEach(m => m.setDim());
    }

    public show(value:boolean):void {
        if(this.multipliers) {
            this.multipliers.forEach((mul) => {
                mul.show(value);
            });
        }
    }

    public playWin(devWay:number):TimelineLite {
        const tl = gsap.timeline();
        const multiplier = this.multipliers[devWay];
        if(!multiplier.forbidden && multiplier.value > 1) {
            tl.to(multiplier.scale, {x : 1.3, y : 1.3, duration : 0.15})
                .to(multiplier.scale, {x : 1, y : 1, duration : 0.25});
        }
        return tl;
    }
}